<script setup lang="ts">
/**
 * The header row of a record page: a status tile, the record's title, a meta
 * line („KD-1042 • Anna Berg • seit 2019“) and the page's controls pushed to
 * the right edge (default slot — a record navigator, „Alle anzeigen“, …).
 * The ERP's page header renders only the ancestor crumb trail and drops the
 * last crumb (I-685); this row renders that crumb as the page title, on record
 * and list pages alike.
 *
 * `status` is a plain string because callers read it off a record column: the
 * tile only shows for the five values NxStatusIcon knows (`STATUS_VALUES`);
 * anything else leaves it out rather than drawing an empty square (I-1596).
 * `find` over `includes` narrows to `StatusValue | undefined` without a cast.
 * `title`, `status` and `meta` may arrive empty while the record loads.
 */
import { computed } from 'vue';
import { NxStatusIcon, STATUS_VALUES } from '../Branding';

const props = defineProps<{
  title?: string;
  status?: string;
  meta?: readonly (string | null | undefined)[];
}>();

const statusTile = computed(() => STATUS_VALUES.find((known) => known === props.status));
const metaText = computed(() => (props.meta ?? []).filter(Boolean).join(' • '));
</script>

<template>
  <div class="nx-record-header" data-testid="nx-record-header">
    <NxStatusIcon v-if="statusTile" :status="statusTile" class="nxt-icon-xl" />
    <div v-if="props.title || metaText" class="nx-record-header__titles">
      <h1 v-if="props.title" class="nx-record-header__title" :title="props.title">
        {{ props.title }}
      </h1>
      <span v-if="metaText" class="nx-record-header__meta" :title="metaText">{{ metaText }}</span>
    </div>
    <div class="nx-record-header__spacer" />
    <div v-if="$slots.default" class="nx-record-header__controls">
      <slot />
    </div>
  </div>
</template>

<style scoped>
.nx-record-header {
  display: flex;
  /* Fills the space its siblings leave instead of claiming 100 %: on list pages
     it shares the header region with the page's buttons and must never squeeze
     them (I-2154). As a block it spans the row on its own anyway. */
  flex: 1 1 0;
  align-items: center;
  gap: var(--nxt-space-md);
  min-width: 0;
}
.nx-record-header__titles {
  display: flex;
  flex: 0 1 auto;
  align-items: baseline;
  gap: var(--nxt-space-sm);
  min-width: 0;
}
.nx-record-header__title {
  min-width: 0;
  margin: 0;
  overflow: hidden;
  color: var(--nxt-fg);
  /* The title is the last breadcrumb: the size NxBreadcrumb `md` gives the
     trail before it (I-2154). */
  font-size: var(--nxt-font-size-sm);
  font-weight: var(--nxt-font-weight-semibold);
  line-height: var(--nxt-line-height-tight);
  text-overflow: ellipsis;
  white-space: nowrap;
}
.nx-record-header__meta {
  flex: 0 1 auto;
  min-width: 0;
  overflow: hidden;
  color: var(--nxt-fg-muted);
  font-size: var(--nxt-font-size-xs);
  text-overflow: ellipsis;
  white-space: nowrap;
}
/* Pushes the slotted controls to the right edge. */
.nx-record-header__spacer {
  flex: 1 1 0;
  min-width: 0;
}
.nx-record-header__controls {
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  gap: var(--nxt-space-xs);
}
</style>
